/**
 * Hook for managing reference data (taxonomies, roles, etc.).
 */

import { useState, useEffect, useCallback } from 'react';
import projectService from '../services/projectService';
import { ReferenceData, Indication } from '../types';

export function useReferenceData() {
  const [referenceData, setReferenceData] = useState<ReferenceData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadReferenceData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await projectService.getReferenceData();
      setReferenceData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load reference data');
      console.error('Error loading reference data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateReferenceData = useCallback(async (data: ReferenceData) => {
    setError(null);
    try {
      await projectService.updateReferenceData(data);
      await loadReferenceData(); // Refresh data
    } catch (err: any) {
      if (projectService.isConcurrencyError(err)) {
        setError('Reference data is being edited by another admin. Please try again.');
        await loadReferenceData(); // Auto-refresh
      } else {
        setError(err.message || 'Failed to update reference data');
      }
      throw err;
    }
  }, [loadReferenceData]);

  // Helper to get indications for a therapeutic area
  const getIndicationsForTherapeuticArea = useCallback(
    (therapeuticAreaCode: string): Indication[] => {
      if (!referenceData) return [];
      return projectService.getIndicationsForTherapeuticArea(
        referenceData,
        therapeuticAreaCode
      );
    },
    [referenceData]
  );

  useEffect(() => {
    loadReferenceData();
  }, [loadReferenceData]);

  return {
    referenceData,
    loading,
    error,
    loadReferenceData,
    updateReferenceData,
    getIndicationsForTherapeuticArea,
  };
}
